import { useFormik } from 'formik'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormLabel,
  CFormSelect,
  CRow,
  CTooltip,
} from '@coreui/react-pro'

import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ToastComponent from 'src/components/common/TaostComponent'
import ScorecardService from 'src/service/ScorecardService'
import CompleteMarkModal from './CompleteMarkModal'
import ClubService from 'src/service/ClubService'
import moment from 'moment'
import Loader from 'src/components/Loader'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft, cilInfo, cilList } from '@coreui/icons'
import MarkAsCompleteModal from './MarkAsCompleteModal'
import LegendInfo from './LegendInfo'
import ScorecardGrid from './ScorecardGrid'
import { hasScores } from './scorecardConfig'
import './ScoreCard.css'

// Two-day fixtures are the only ones played over two innings - `match_type` 28 in
// dropdown_managers, which the API also sends back by name. Either spelling is
// accepted so the screen does not depend on which of the two the endpoint fills in.
const isTwoDayFixture = (fixtureDetails) =>
  Number(fixtureDetails?.match_type) === 28 || fixtureDetails?.match_type_name === '2-Day'

const INNINGS = [
  { value: 1, label: '1st Innings' },
  { value: 2, label: '2nd Innings' },
]

const sumStat = (rows, key) =>
  (rows || []).reduce((total, row) => {
    const value = Number(row?.[key])
    return Number.isFinite(value) ? total + value : total
  }, 0)

const MATCH_RESULTS = { 1: 'Won', 2: 'Other' }

const CommonFullScoreCard = () => {
  const [scorecardColoumns, setScorecardColoumns] = useState([])
  const navigate = useNavigate()
  const [visible, setVisible] = useState(false)
  const [completMarkModalVisible, setCompletMarkModalVisible] = useState(false)
  const [finalScorecardModalVisible, setFinalScorecardModalVisible] = useState(false)
  const [defaultInning, setDefaultInning] = useState(1)
  const [scorecardDetails, setScorecardDetails] = useState([])
  const [fixtureDetails, setFixtureDetails] = useState({})
  const [loader, setLoader] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [statusValue, setStatusValue] = useState()
  const param = useParams()
  // Which team's scorecard is on screen. Set from the fixture once it loads, so
  // the home side is the one that opens.
  const [activeTeamId, setActiveTeamId] = useState(null)

  const fetchData = async (defaultInning = 1) => {
    setScorecardDetails([])
    setLoader(true)
    try {
      const scorecardResponse = await ScorecardService.fullScorecardDetail(
        param.fixtureId,
        defaultInning,
      )
      if (scorecardResponse.status === 200) {
        setScorecardDetails(scorecardResponse.data)
        setScorecardColoumns(scorecardResponse.scorecardColoumns)
        if (scorecardResponse.player_list.length < 1) {
          ToastComponent(
            'Please add a minimum of 1 player to the squad list of this fixture!',
            'error',
          )
          navigate(`/manage-scores/team-players/${param.fixtureId}`)
          return // Exit early if player list is insufficient
        }
        setFixtureDetails(scorecardResponse.fixtureDetails)
        setStatusValue(scorecardResponse.fixtureDetails.status)
      }

      // The game itself is not read on this screen - the call stays because it is
      // what the rest of the scorecard screens gate their first paint on.
      const clubResponse = await ClubService.getClubDetails()
      if (clubResponse.status === 200) {
        setLoader(false)
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setLoader(false)
      setIsLoading(false)
      // Handle error here, e.g., show an error toast
    }
  }

  useEffect(() => {
    fetchData()
    // Cleanup (if necessary)
    return () => {
      // Cleanup code here (if any)
    }
  }, [])
  const formik = useFormik({
    initialValues: {
      data: scorecardDetails,
      scorcard_link_name: fixtureDetails?.scorcard_link_name,
      scorcard_link_url: fixtureDetails?.scorcard_link_url,
      fall_of_wickets: fixtureDetails?.fall_of_wickets,
      match_report: fixtureDetails?.match_report,
      potm_match_status: fixtureDetails?.potm_match_status ?? 0,
      potm_match_status2: fixtureDetails?.potm_match_status2 ?? 0,
      potm: fixtureDetails?.potm ?? [], // for Team 1
      potm2: fixtureDetails?.potm2 ?? [], // for Team 2
      match_status: '',
    },
    enableReinitialize: true,
    onSubmit: (data, actions) => {
      data.fixtureId = param.fixtureId
      data.status = statusValue
      data.inning = defaultInning
      setLoader(true)
      ScorecardService.saveScorecard(data).then((res) => {
        if (res.status === 200) {
          fetchData(defaultInning)
          setLoader(false)
          ToastComponent(res.message, 'success')
        }
      })
    },
  })
  const [completeMarkModalData, setCompleteMarkModalData] = useState([])
  const handleMatchStatus = (e) => {
    setStatusValue(e.target.value)
    const data = {}
    data.fixtureId = param.fixtureId
    data.status = e.target.value
    if (e.target.value === '2') {
      ScorecardService.getSavedScorecardData(data).then((res) => {
        if (res.status === 200) {
          setCompleteMarkModalData(res.data)
          setCompletMarkModalVisible(true)
        }
      })
    } else {
      // formikRef.current.handleSubmit()
      ScorecardService.changFixtureStatus(param.fixtureId, e.target.value)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            navigate(`/full-scorecard/${param.fixtureId}`)
          }
        })
        .catch((e) => {
          console.log('e', e)
          ToastComponent('Something went wrong.', 'error')
        })
    }
  }

  const formikRef = React.useRef()
  useEffect(() => {
    formikRef.current = formik
  }, [formik])

  const changeInning = (inning) => {
    if (Number(inning) === Number(defaultInning)) return
    setDefaultInning(inning)
    fetchData(inning)
  }

  const [isMobile, setIsMobile] = useState(false)
  // Function to update the isMobile state based on the screen width
  const updateScreenSize = () => {
    setIsMobile(window.innerWidth <= 768) // Adjust the breakpoint as needed
  }
  // Use useEffect to update the screen size state on component mount and window resize
  useEffect(() => {
    updateScreenSize() // Initial check
    window.addEventListener('resize', updateScreenSize)

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', updateScreenSize)
    }
  }, [])
  function formatFantasyPoints(fantasyPoints) {
    // Check if there are decimal digits
    if (fantasyPoints % 1 !== 0) {
      return fantasyPoints.toFixed(2).replace(/0+$/, '') // Remove trailing zeros
    } else {
      return fantasyPoints.toString() // Convert to a string without decimal places
    }
  }
  const [infoPageModal, setInfoPageModal] = useState(false)
  const handleInfoClick = () => {
    setInfoPageModal(true)
  }

  const handleInputChange = (event, type) => {
    formik.handleChange(event) // Update formik state
    const data = {}
    data.fixtureId = param.fixtureId
    data.type = type
    data.value = event.target.value
    ScorecardService.updateCustomScorecardField(data).then((res) => {
      if (res.status === 200) {
        setLoader(false)
        ToastComponent(res.message, 'success')
      }
    })
  }

  // ---- presentation helpers ------------------------------------------------

  const twoDay = isTwoDayFixture(fixtureDetails)

  // One row per side of the fixture. `fixtures.team` is the home team and
  // `fixtures.team2` the away one (that is the order the fixture import writes
  // them in), so the home side is always the first - and therefore the open - tab.
  const teams = useMemo(() => {
    const list = []
    if (fixtureDetails?.team) {
      list.push({
        id: String(fixtureDetails.team),
        name: fixtureDetails.team_name || 'Team 1',
        home: true,
        matchResult: fixtureDetails.potm_match_status,
      })
    }
    if (fixtureDetails?.team2) {
      list.push({
        id: String(fixtureDetails.team2),
        name: fixtureDetails.team2_name || 'Team 2',
        home: false,
        matchResult: fixtureDetails.potm_match_status2,
      })
    }
    return list
  }, [fixtureDetails])

  // Rows carry the team they were saved against, so the combined innings splits
  // cleanly in the browser - no second request per team.
  const rowsByTeam = useMemo(() => {
    const grouped = new Map()
    ;(scorecardDetails || []).forEach((row) => {
      const key = String(row?.team_id ?? '')
      if (!grouped.has(key)) grouped.set(key, [])
      grouped.get(key).push(row)
    })
    return grouped
  }, [scorecardDetails])

  // Anything saved against a team the fixture no longer names still has to be
  // reachable, so it is swept into one last tab rather than dropped.
  const tabs = useMemo(() => {
    const known = new Set(teams.map((team) => team.id))
    const list = teams.map((team) => ({ ...team, rows: rowsByTeam.get(team.id) || [] }))
    const orphans = []
    rowsByTeam.forEach((rows, teamId) => {
      if (!known.has(teamId)) orphans.push(...rows)
    })
    if (orphans.length) {
      list.push({ id: 'unassigned', name: 'Other players', home: false, rows: orphans })
    }
    return list
  }, [teams, rowsByTeam])

  useEffect(() => {
    if (!tabs.length) return
    if (!tabs.some((tab) => tab.id === activeTeamId)) setActiveTeamId(tabs[0].id)
  }, [tabs, activeTeamId])

  const activeTab = tabs.find((tab) => tab.id === activeTeamId) || tabs[0]
  const activeRows = activeTab?.rows || []

  const enteredCount = activeRows.filter((item) => hasScores(item, scorecardColoumns)).length
  const playerCount = activeRows.length
  const enteredPercent = playerCount ? Math.round((enteredCount / playerCount) * 100) : 0

  const hasColumn = (key) => (scorecardColoumns || []).some((c) => c?.attribute_key === key)
  const teamRuns = sumStat(activeRows, 'rs')
  const teamWickets = sumStat(activeRows, 'wks')
  const teamPoints = formatFantasyPoints(sumStat(activeRows, 'fantasy_points'))
  const matchResult = MATCH_RESULTS[String(activeTab?.matchResult ?? '')]

  const matchTitle = fixtureDetails?.team2_name
    ? `${fixtureDetails.team_name} v ${fixtureDetails.team2_name}`
    : fixtureDetails?.team_name

  const scorecardTitle = fixtureDetails.scorcard_link_url ? (
    <Link to={fixtureDetails.scorcard_link_url} target="_blank">
      Player stats
    </Link>
  ) : (
    'Player stats'
  )

  return (
    <>
      <CForm onSubmit={formik.handleSubmit}>
        <div className="ge-page-header">
          <button type="button" className="ge-back-link" onClick={() => navigate(-1)}>
            <CIcon icon={cilArrowLeft} />
            Manage Scores
          </button>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
            <div>
              <h1 className="ge-page-title">Full scorecard &mdash; {matchTitle}</h1>
              <p className="ge-page-subtitle">
                The saved scores for this fixture, read only. Pick a team to see its scorecard
                {twoDay ? ' and an innings to see how that innings was scored' : ''}. Edit scores
                from Manage Scores.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {activeTab && activeTab.id !== 'unassigned' && (
                <CButton
                  color="secondary"
                  variant="outline"
                  href={`/manage-scores/team-players/${param.fixtureId}/${activeTab.id}`}
                >
                  <CIcon icon={cilList} className="me-1" />
                  Squad
                </CButton>
              )}
              <CButton color="secondary" variant="outline" onClick={handleInfoClick}>
                <CIcon icon={cilInfo} className="me-1" />
                Column legend
              </CButton>
            </div>
          </div>
        </div>

        <div className="ge-meta-strip">
          <div className="ge-meta-item">
            <span className="ge-meta-label">Round</span>
            <span className="ge-meta-value">{fixtureDetails.round}</span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Home</span>
            <span className="ge-meta-value">{fixtureDetails.team_name}</span>
          </div>
          {fixtureDetails?.team2_name && (
            <div className="ge-meta-item">
              <span className="ge-meta-label">Away</span>
              <span className="ge-meta-value">{fixtureDetails.team2_name}</span>
            </div>
          )}
          {fixtureDetails?.match_type_name && (
            <div className="ge-meta-item">
              <span className="ge-meta-label">Format</span>
              <span className="ge-meta-value">{fixtureDetails.match_type_name}</span>
            </div>
          )}
          <div className="ge-meta-item">
            <span className="ge-meta-label">Starts</span>
            <span className="ge-meta-value">
              {moment(fixtureDetails.start_date).format('D MMM YYYY')}
              {', '}
              {moment(fixtureDetails.start_time, ['HH:mm']).format('h:mma')}
            </span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Ends</span>
            <span className="ge-meta-value">
              {moment(fixtureDetails.end_date).format('D MMM YYYY')}
              {', '}
              {moment(fixtureDetails.end_time, ['HH:mm']).format('h:mma')}
            </span>
          </div>
        </div>

        {/* One tab per side, home first. A single-team fixture gets no strip -
            there would be nothing to switch between. */}
        {tabs.length > 1 && (
          <div className="ge-tabs" role="tablist" aria-label="Team scorecards">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab?.id === tab.id}
                className={`ge-tab${activeTab?.id === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTeamId(tab.id)}
              >
                {tab.name}
                {tab.home && <span className="text-medium-emphasis fw-normal small">(Home)</span>}
                <span className="ge-tab-count">{tab.rows.length}</span>
              </button>
            ))}
          </div>
        )}

        <div className="sc-toolbar">
          <div className="d-flex flex-wrap align-items-center gap-3">
            {/* A one-day fixture is a single innings per side, so there is nothing
                to choose - the innings control only appears for two-day games. */}
            {twoDay && (
              <div className="d-flex align-items-center gap-2">
                <span className="small fw-semibold text-medium-emphasis">Innings</span>
                <div className="ge-segmented-tabs" role="tablist" aria-label="Select innings">
                  {INNINGS.map((innings) => (
                    <button
                      key={innings.value}
                      type="button"
                      role="tab"
                      aria-selected={Number(defaultInning) === innings.value}
                      className={`nav-link${
                        Number(defaultInning) === innings.value ? ' active' : ''
                      }`}
                      onClick={() => changeInning(innings.value)}
                    >
                      {innings.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="small fw-medium text-medium-emphasis">
              {enteredCount} of {playerCount} players have scores
            </span>
            <div className="sc-progress-track">
              <div className="sc-progress-fill" style={{ width: `${enteredPercent}%` }} />
            </div>
          </div>
        </div>

        <CCard className="mb-4">
          <CCardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <strong>
              {scorecardTitle}
              {activeTab && <> &mdash; {activeTab.name}</>}
            </strong>
            {twoDay && (
              <span className="small text-medium-emphasis">
                {INNINGS.find((innings) => innings.value === Number(defaultInning))?.label}
              </span>
            )}
          </CCardHeader>
          <CCardBody>
            {loader || isLoading ? (
              <Loader />
            ) : (
              <>
                {/* What the innings adds up to for this side, so the totals do not
                    have to be read off the grid column by column. */}
                <div className="ge-meta-strip">
                  {hasColumn('rs') && (
                    <div className="ge-meta-item">
                      <span className="ge-meta-label">Batting runs</span>
                      <span className="ge-meta-value">{teamRuns}</span>
                    </div>
                  )}
                  {hasColumn('wks') && (
                    <div className="ge-meta-item">
                      <span className="ge-meta-label">Wickets taken</span>
                      <span className="ge-meta-value">{teamWickets}</span>
                    </div>
                  )}
                  <div className="ge-meta-item">
                    <span className="ge-meta-label">Fantasy points</span>
                    <span className="ge-meta-value">{teamPoints}</span>
                  </div>
                  <div className="ge-meta-item">
                    <span className="ge-meta-label">Players scored</span>
                    <span className="ge-meta-value">
                      {enteredCount} of {playerCount}
                    </span>
                  </div>
                  {matchResult && (
                    <div className="ge-meta-item">
                      <span className="ge-meta-label">Match result</span>
                      <span className="ge-meta-value">{matchResult}</span>
                    </div>
                  )}
                </div>

                <ScorecardGrid
                  columns={scorecardColoumns}
                  rows={activeRows}
                  isMobile={isMobile}
                  readOnly
                  formatFantasyPoints={formatFantasyPoints}
                  emptyLabel={`No scores saved for ${activeTab?.name ?? 'this team'}${
                    twoDay ? ' in this innings' : ''
                  } yet.`}
                />
              </>
            )}
          </CCardBody>
        </CCard>

        <CCard className="mb-4">
          <CCardHeader>
            <strong>Scorecard Details</strong>
            <div className="text-medium-emphasis small">
              Where this scorecard came from. Both fields save as you type.
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow className="g-3">
              <CCol md={6}>
                <CFormLabel htmlFor="scorcard_link_name">Scorecard Source</CFormLabel>
                <input
                  type="text"
                  name="scorcard_link_name"
                  className={'form-control'}
                  id="scorcard_link_name"
                  placeholder="Scorecard Source"
                  defaultValue={formik.values.scorcard_link_name}
                  onChange={(e) => handleInputChange(e, 'scorcard_link_name')}
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="scorcard_link_url">Scorecard Link</CFormLabel>
                <input
                  type="text"
                  name="scorcard_link_url"
                  className={'form-control'}
                  id="scorcard_link_url"
                  placeholder="Scorecard Link"
                  defaultValue={formik.values.scorcard_link_url}
                  onChange={(e) => handleInputChange(e, 'scorcard_link_url')}
                />
              </CCol>

              {/* <CCol md={6}>
                <CFormLabel htmlFor="fall_of_wickets">Fall of Wickets</CFormLabel>
                <CFormTextarea
                  aria-label="With textarea"
                  defaultValue={formik.values.fall_of_wickets}
                  name={'fall_of_wickets'}
                  onChange={(e) => handleInputChange(e, 'fall_of_wickets')}
                ></CFormTextarea>
              </CCol> */}
            </CRow>
          </CCardBody>
        </CCard>

        <CCard className="mt-3">
          <CCardHeader>
            <strong>Match status</strong>
            <div className="text-medium-emphasis small">
              The last step - choosing Completed opens a final review of the saved scorecard.
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md={6}>
                <div className="d-flex align-items-center gap-2">
                  <CFormSelect
                    aria-label="Select Structure"
                    name="match_status"
                    value={statusValue}
                    id="match_status"
                    onChange={handleMatchStatus}
                  >
                    <option value={0}>Select Match Status </option>
                    <option value={1}>In-Progress</option>
                    <option value={2}>Completed</option>
                  </CFormSelect>
                  <CTooltip content="What the match statuses mean">
                    <CButton
                      color="secondary"
                      variant="ghost"
                      onClick={() => setVisible(!visible)}
                      aria-label="Match status help"
                    >
                      <CIcon icon={cilInfo} />
                    </CButton>
                  </CTooltip>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CForm>

      <CompleteMarkModal visible={visible} setVisible={setVisible} />
      <MarkAsCompleteModal
        completMarkModalVisible={completMarkModalVisible}
        setCompletMarkModalVisible={setCompletMarkModalVisible}
        data={completeMarkModalData}
        fixtureId={param.fixtureId}
        finalScorecardModalVisible={finalScorecardModalVisible}
        setFinalScorecardModalVisible={setFinalScorecardModalVisible}
      />
      <LegendInfo infoPageModal={infoPageModal} setInfoPageModal={setInfoPageModal} />
    </>
  )
}

export default CommonFullScoreCard
